/**
 * event.config.ts — the ONLY file that normally needs editing per event.
 *
 * Two kinds of value live here:
 *   1. buildTime  — branding + infra ids, baked into the web bundle and functions.
 *   2. seed       — pushed into Firestore `config/event` by `scripts/import-event.ts`.
 *                   At runtime the server ALWAYS reads `config/event`, never this file,
 *                   so prices/closeAt/status can be changed without a redeploy.
 */
import type { EventConfigInput } from './shared/src/schemas/event.js'

/**
 * The manufacturer's standard chart (Hybrid Sport), in inches, transcribed once.
 *
 * Both styles sell the same range, so the list lives here and each item points at
 * it rather than repeating fifteen rows twice.
 */
const HYBRID_SIZE_CHART = [
  { code: '3S', chest: 32, length: 24 },
  { code: '2S', chest: 34, length: 25 },
  { code: 'S', chest: 36, length: 26 },
  { code: 'M', chest: 38, length: 27 },
  { code: 'L', chest: 40, length: 28 },
  { code: 'XL', chest: 42, length: 29 },
  { code: '2XL', chest: 44, length: 30 },
  { code: '3XL', chest: 46, length: 31 },
  { code: '4XL', chest: 48, length: 31 },
  { code: '5XL', chest: 50, length: 31 },
  { code: '6XL', chest: 52, length: 31 },
  { code: '7XL', chest: 54, length: 32 },
  { code: '8XL', chest: 56, length: 33 },
  { code: '9XL', chest: 58, length: 34 },
  { code: '10XL', chest: 60, length: 35 },
] as const

/** The maker charges more for these, so the buyer is charged more for these. */
const SPECIAL_SIZES = ['4XL', '5XL', '6XL', '7XL', '8XL', '9XL', '10XL'] as const
const SPECIAL_SIZE_EXTRA = 100

/** Measurements and price in one row, which is how the organiser edits them in /admin. */
const sizes = HYBRID_SIZE_CHART.map((row) => ({
  ...row,
  extra: (SPECIAL_SIZES as readonly string[]).includes(row.code) ? SPECIAL_SIZE_EXTRA : 0,
}))

/** Printed under the table: the maker states this, so the buyer should read it too. */
const SIZE_NOTE = 'รอบอกและความยาวคลาดเคลื่อนได้ ±1 นิ้ว'

/** Both styles are the same garment with and without sleeves, and cost the same. */
/* Pre-VAT, as the factory quotes it: 350 less 35%. VAT is added at checkout. */
const SHIRT_PRICE = 263

export const buildTime = {
  projectId: 'jaikrob-avp-run-2026',
  region: 'asia-southeast1',
  subdomain: 'avp-run.jaikrob.com',
  /**
   * The one place the brand colour is written.
   *
   * Tailwind reads it for the `brand` colour, a Vite plugin injects it into
   * index.html's theme-color and generates favicon.svg from it, and the mock
   * checkout page imports it. Changing this line changes all of them.
   */
  primaryColor: '#1d4ed8',
  mainSiteUrl: 'https://jaikrob.com',
  /**
   * Local dev port. Named here because three places need to agree on it — Vite's
   * server, the callables' CORS allowlist, and the returnUrl the payment provider
   * sends the buyer back to. A mismatch shows up as a CORS failure at checkout.
   */
  devWebPort: 5180,
} as const

export const seed: EventConfigInput = {
  type: 'product',
  title: 'เสื้อวิ่ง AVP Run 2026',
  subtitle: 'พรีออเดอร์ — ผลิตตามจำนวนสั่ง ไม่มีสต็อก',
  coverImage: '/images/SS.jpg',
  closeAt: '2026-10-10T16:59:59.000Z', // 11 Oct 2026 00:00 น. (Asia/Bangkok)
  status: 'open',
  orderPrefix: 'AVP',
  // Beam caps the QR at 30 minutes; 15 more covers a webhook that arrives late.
  pendingExpiryMinutes: 45,
  contactEmail: 'drsuwatlive1992@gmail.com',
  /**
   * Must be a VERIFIED sender in Brevo, otherwise every send is rejected.
   * The reply-to is set to `contactEmail` at send time, so replies reach the
   * organiser rather than this address.
   */
  sender: { name: 'AVP Run 2026', email: 'noreply@jaikrob.com' },
  /*
   * VAT already inside the shirt price — the factory's, not ours.
   *
   * The organiser is NOT VAT-registered: this is shown so a buyer can see what the
   * price is made of, and must never be presented as tax we collect or can invoice.
   */
  vatRate: 0.07,
  shipping: { enabled: true, fee: 60 },
  items: [
    {
      sku: 'SS',
      name: 'เสื้อแขนสั้น',
      basePrice: SHIRT_PRICE,
      image: '/images/SS.jpg',
      sizeNote: SIZE_NOTE,
      /*
       * One colourway, so there is nothing to choose: an empty list makes the order
       * form drop the colour row entirely rather than showing a picker with a single
       * option, and the order line then carries no colour.
       */
      colors: [],
      sizes: sizes.map((size) => ({ ...size })),
    },
    {
      sku: 'SL',
      name: 'เสื้อแขนกุด',
      basePrice: SHIRT_PRICE,
      image: '/images/SL.jpg',
      sizeNote: SIZE_NOTE,
      colors: [],
      sizes: sizes.map((size) => ({ ...size })),
    },
  ],
}

/**
 * Empty on purpose.
 *
 * The real roster lives in Firestore, imported at /admin/participants. It is NOT
 * mirrored here, because `import-event` upserts every name in this array with
 * `deletedAt: null` — so a name left here is resurrected by the next seed, even one
 * an organiser deliberately removed. Seeding must never undo a deletion made in the
 * admin UI, and re-seeding to change a price is a normal thing to do mid-event.
 *
 * Only put names here for a brand-new event that has no roster yet.
 */
export const participantNames: string[] = []
